import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiFileText, FiMonitor, FiLink, FiInfo, FiChevronRight, FiChevronDown, FiActivity, FiArrowLeft } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

export default function StudentPackContentView() {
    const { packId } = useParams();
    const navigate = useNavigate();
    const [pack, setPack] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeItem, setActiveItem] = useState(null);
    const [securitySettings, setSecuritySettings] = useState(null);

    useEffect(() => {
        const loadPackDetails = async () => {
            try {
                const [packData, settingsData] = await Promise.all([
                    fetchAPI(`/admin/get_study_pack_details.php?id=${packId}`),
                    fetchAPI('/admin/get_settings.php')
                ]);
                setPack(packData);
                setSecuritySettings(settingsData);
                // Set first free item as active if available
                const firstFree = packData.contents?.find(c => c.is_free);
                if (firstFree) setActiveItem(firstFree);
            } catch (err) {
                console.error("Failed to load pack content", err);
            } finally {
                setLoading(false);
            }
        };
        loadPackDetails();
    }, [packId]);

    const getEmbedUrl = (url) => {
        if (!url) return null;
        const ytRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const ytMatch = url.match(ytRegex);
        if (ytMatch && ytMatch[2].length === 11) {
            return `https://www.youtube.com/embed/${ytMatch[2]}?rel=0&modestbranding=1&showinfo=0&autoplay=1`;
        }
        const vimeoRegex = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
        const vimeoMatch = url.match(vimeoRegex);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`;
        }
        return url;
    };

    if (loading) return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Initializing Content View...</p>
        </div>
    );

    if (!pack) return <div className="p-8 text-center text-red-500 font-bold">Module not found</div>;

    return (
        <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
            {/* SyZyGy Style Header */}
            <div className="h-16 md:h-20 bg-blue-600 flex items-center justify-between px-6 shadow-xl z-20">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-white/10 text-white rounded-xl transition-all"
                    >
                        <FiArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-white text-sm md:text-lg font-black uppercase tracking-tight italic">
                            {pack.title} <span className="text-blue-200">({pack.subject})</span>
                        </h1>
                        <p className="text-blue-100 text-[8px] md:text-[10px] font-bold uppercase tracking-widest opacity-80">
                            Maths with Manoj • Study Pack Environment
                        </p>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-3">
                    <div className="h-10 w-px bg-white/20"></div>
                    <div className="text-right">
                        <p className="text-white text-[10px] font-black uppercase tracking-widest leading-none mb-1">Status</p>
                        <p className="text-blue-200 text-[8px] font-bold uppercase tracking-widest leading-none">Free Access Mode</p>
                    </div>
                </div>
            </div>

            {/* Split Layout */}
            <div className="flex flex-1 overflow-hidden relative">
                {/* Left Sidebar: Lessons Tree */}
                <div className="w-full md:w-80 lg:w-96 bg-white border-r border-gray-200 flex flex-col shadow-inner z-10 overflow-y-auto">
                    <div className="p-4 md:p-6 space-y-4">
                        <button className="w-full py-3 bg-gray-100/50 hover:bg-gray-100 text-gray-700 text-[11px] font-black rounded-xl border border-gray-200/50 flex items-center justify-center gap-2 uppercase tracking-widest transition-all">
                            <FiInfo size={14} /> Show Details
                        </button>

                        <div className="space-y-6">
                            {/* For Study Packs we might not have 'Lessons' hierarchy like months, 
                                but we can group them or list them directly as shown in the image */}
                            <div className="space-y-4">
                                {pack.contents?.map((item, idx) => (
                                    <div key={idx} className="space-y-3">
                                        <button
                                            onClick={() => item.is_free && setActiveItem(item)}
                                            className={`w-full group text-left p-3 rounded-2xl border transition-all flex items-center gap-3 ${activeItem === item
                                                    ? 'bg-blue-50 border-blue-200'
                                                    : item.is_free
                                                        ? 'bg-white border-gray-100 hover:border-gray-200'
                                                        : 'bg-gray-50 border-transparent opacity-60 cursor-not-allowed'
                                                }`}
                                        >
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${activeItem === item ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'
                                                }`}>
                                                {item.type === 'pdf' ? <FiFileText size={16} /> : <FiMonitor size={16} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className={`text-[11px] font-black uppercase tracking-wide truncate ${activeItem === item ? 'text-blue-700' : 'text-gray-900 font-bold'
                                                    }`}>
                                                    {item.title}
                                                </h4>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{item.type}</span>
                                                    {!item.is_free && (
                                                        <span className="text-[7px] font-black text-red-500 uppercase tracking-widest">Locked</span>
                                                    )}
                                                </div>
                                            </div>
                                            {item.is_free && <FiChevronRight className={`transition-transform duration-300 ${activeItem === item ? 'rotate-90 text-blue-600' : 'text-gray-300'}`} />}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-auto p-6 border-t border-gray-100 bg-gray-50/50">
                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.2em] text-center">
                            Powered by SyZyGy Learning SDK
                        </p>
                    </div>
                </div>

                {/* Right: Main Content Player */}
                <div className="flex-1 bg-gray-900 relative">
                    {activeItem ? (
                        <div className="w-full h-full flex flex-col">
                            {activeItem.type === 'pdf' ? (
                                <iframe
                                    src={activeItem.url.includes('drive.google.com') ? activeItem.url.replace(/\/view.*$/, '/preview') : activeItem.url}
                                    className="w-full h-full border-none"
                                    title={activeItem.title}
                                ></iframe>
                            ) : (
                                <div className="w-full h-full relative group">
                                    <iframe
                                        src={getEmbedUrl(activeItem.url)}
                                        className="w-full h-full border-none"
                                        allowFullScreen
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        title={activeItem.title}
                                    ></iframe>

                                    {/* Watermark Overlay */}
                                    {securitySettings?.screen_recording_watermark === '1' && (
                                        <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden opacity-10">
                                            <span className="absolute text-[12px] text-white/20 font-black uppercase rotate-[-30deg] select-none whitespace-nowrap" style={{ top: '20%', left: '10%' }}>
                                                MANOJ MATHS • SECURE MEDIA
                                            </span>
                                            <span className="absolute text-[12px] text-white/20 font-black uppercase rotate-[-30deg] select-none whitespace-nowrap" style={{ bottom: '25%', right: '15%' }}>
                                                MANOJ MATHS • SECURE MEDIA
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-center p-12">
                            <div className="w-24 h-24 bg-white/5 rounded-[2rem] flex items-center justify-center mb-6 text-white/10 animate-pulse">
                                <FiActivity size={48} />
                            </div>
                            <h2 className="text-xl font-black text-white italic uppercase tracking-tighter mb-2">Select an entry to begin</h2>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-widest max-w-md mx-auto">
                                Explore the Study Pack curriculum by selecting an unlocked component from the sidebar menu.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
